import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import PICreator from "../elements/PICreator";
import API from "../../services/api";
import { Link } from "react-router-dom";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE = `${process.env.REACT_APP_API_URL}/api/clients`;

const TOAST_CFG = {
  position: "top-center",
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light",
};

// ─── GLOBAL CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { background: #ffffff; color: #1a1a2e; font-family: 'Poppins', sans-serif; }
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: #f8f8ff; }
::-webkit-scrollbar-thumb { background: #b8b8d4; border-radius: 2px; }
@keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes scaleIn { from { opacity:0; transform:scale(0.96) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }
@keyframes spin    { to { transform: rotate(360deg); } }
@keyframes shimmer { 0%,100%{opacity:.35} 50%{opacity:.65} }
.anim-fade-up  { animation: fadeUp  .3s ease both; }
.anim-fade-in  { animation: fadeIn  .2s ease both; }
.anim-scale-in { animation: scaleIn .25s cubic-bezier(.16,1,.3,1) both; }
.spin          { animation: spin 0.75s linear infinite; }
.row-enter     { animation: fadeUp .25s ease both; }
`;

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const RAJ  = { fontFamily: "'Poppins',sans-serif" };
const MONO = { fontFamily: "'Poppins', sans-serif" };
const SANS = { fontFamily: "'Poppins',sans-serif" };

const INPUT_CLS =
  "w-full bg-[#ffffff] border border-[#d0d0e8] rounded-[6px] px-[11px] py-[9px] " +
  "text-[#1a1a2e] text-[12px] outline-none transition-all duration-150 placeholder-[#4a4a75] " +
  "focus:border-[#7c6dfa] focus:shadow-[0_0_0_2px_rgba(124,109,250,0.13)] " +
  "disabled:opacity-40 disabled:cursor-not-allowed";

const INPUT_ERR =
  "border-[#f05060] focus:border-[#f05060] focus:shadow-[0_0_0_2px_rgba(240,80,96,0.13)]";

const EMPTY_FORM = {
  companyName:    "",
  gstin:          "",
  address:        "",
  email:          "",
  mobile:         "",
  shipCompanyName:"Same as above or different",
  shipPhone:      "",
  shipAddress:    "",
  contactPerson: { name: "", number: "" },
};

// ─── SECTION LABEL ────────────────────────────────────────────────────────────
function SectionLabel({ children, divider = false }) {
  return (
    <p
      className={`text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75] mb-3 ${divider ? "border-t border-[#d0d0e8] pt-5" : ""}`}
      style={MONO}
    >{children}</p>
  );
}

// ─── FIELD LABEL ─────────────────────────────────────────────────────────────
function FieldLabel({ label, required }) {
  return (
    <label className="text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75]" style={MONO}>
      {label}
      {label === "GSTIN" && (
              <Link
                to="https://cleartax.in/gst-number-search/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1ea01e] hover:underline ml-[10px] text-[9px]"
              >
                Link
              </Link>
            )}
      {required && <span className="text-[#f0c040] ml-[2px]">*</span>}
    </label>
  );
}

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────
function ConfirmDialog({ open, clientName, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center anim-fade-in" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative anim-scale-in bg-[#f8f8ff] border border-[#b8b8d4] rounded-xl p-7 w-[340px] shadow-[0_24px_60px_rgba(0,0,0,.7)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#f05060] to-[#7c6dfa] rounded-t-xl" />
        <div className="text-[28px] mb-3 text-center">⚠️</div>
        <h3 className="text-[16px] font-bold text-[#1a1a2e] text-center mb-1" style={RAJ}>Delete Client?</h3>
        <p className="text-[12px] text-[#4a4a75] text-center mb-6" style={MONO}>
          <span className="text-[#7c6dfa]">{clientName}</span> will be permanently removed.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-[9px] rounded-[7px] border border-[#b8b8d4] text-[#1a1a2e] text-[13px] font-semibold hover:border-[#7c6dfa] transition-all"
            style={RAJ}
          >Cancel</button>
          <button
            onClick={onConfirm}
            className="flex-1 py-[9px] rounded-[7px] bg-[#f05060] text-white text-[13px] font-semibold hover:bg-[#f07080] hover:-translate-y-px transition-all"
            style={RAJ}
          >Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── SKELETON ROW ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="border-b border-[rgba(208,208,232,0.7)]">
      {[30, 140, 110, 90, 100, 90, 110, 90, 110, 80].map((w, i) => (
        <td key={i} className="px-3 py-[13px]">
          <div
            className="rounded-[4px] bg-[#eeeef6] h-3"
            style={{ width: w, animation: `shimmer 1.6s ease-in-out ${i * 0.07}s infinite` }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── CLIENT MODAL ─────────────────────────────────────────────────────────────
// All field components are defined OUTSIDE to prevent focus loss on re-render
function ClientModal({ open, mode, initial, onClose, onSave, saving }) {
  const [form, setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        companyName:     initial.companyName     ?? "",
        gstin:           initial.gstin           ?? "",
        address:         initial.address         ?? "",
        email:           initial.email           ?? "",
        mobile:          initial.mobile          ?? "",
        shipCompanyName: initial.shipCompanyName ?? "",
        shipPhone:       initial.shipPhone       ?? "",
        shipAddress:     initial.shipAddress     ?? "",
        contactPerson: {
          name:   initial.contactPerson?.name   ?? "",
          number: initial.contactPerson?.number ?? "",
        },
      });
    } else {
      setForm({ ...EMPTY_FORM, contactPerson: { name: "", number: "" } });
    }
    setErrors({});
  }, [open, mode, initial]);

  useEffect(() => {
    if (!open) return;
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open, onClose]);

  // Top-level field updater
  const setField = (field) => (val) => {
    setForm(prev => ({ ...prev, [field]: val }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  // Nested contactPerson updater
  const setContact = (subField) => (val) => {
    setForm(prev => ({ ...prev, contactPerson: { ...prev.contactPerson, [subField]: val } }));
    setErrors(prev => ({ ...prev, [`contactPerson.${subField}`]: "" }));
  };

  const validate = () => {
    const errs = {};
    const topRequired = ["companyName","gstin","address","email","mobile","shipCompanyName","shipPhone","shipAddress"];
    topRequired.forEach(k => {
      if (!form[k]?.trim()) errs[k] = "Required";
    });
    if (!form.contactPerson.name?.trim())   errs["contactPerson.name"]   = "Required";
    if (!form.contactPerson.number?.trim()) errs["contactPerson.number"] = "Required";

    // Basic email check
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Invalid email format";

    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave(form);
  };

  if (!open) return null;

  // Inline input helper — plain function, not a component, so no unmount on re-render
  const inp = (field, placeholder = "", type = "text", isContact = false, subField = null) => {
    const value    = isContact ? form.contactPerson[subField] : form[field];
    const errKey   = isContact ? `contactPerson.${subField}` : field;
    const onChange = isContact ? setContact(subField) : setField(field);
    return (
      <input
        className={`${INPUT_CLS} ${errors[errKey] ? INPUT_ERR : ""}`}
        style={MONO}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
      />
    );
  };

  const Err = ({ k }) => errors[k]
    ? <span className="text-[10px] text-[#f05060]" style={MONO}>{errors[k]}</span>
    : null;

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center anim-fade-in" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      <div
        className="relative anim-scale-in bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl w-[760px] max-w-[96vw] max-h-[92vh] flex flex-col shadow-[0_32px_80px_rgba(0,0,0,.8)] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#40c0f0] via-[#7c6dfa] to-[#f0c040]" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d0d0e8] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-[#40c0f0] shadow-[0_0_8px_#40c0f0]" />
            <span className="text-[13px] font-bold tracking-[3px] uppercase text-[#4a4a75]" style={RAJ}>
              {mode === "edit" ? "Edit Client" : "Add New Client"}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full border border-[#b8b8d4] text-[#4a4a75] hover:border-[#f05060] hover:text-[#f05060] transition-all text-[14px]"
          >✕</button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-6">

          {/* ── Bill To / Company Info ── */}
          <div>
            <SectionLabel>Company Information</SectionLabel>
            <div className="grid grid-cols-2 gap-3">

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Company Name" required />
                {inp("companyName", "DIGAMBARA REINFORCEMENT PRIVATE LIMITED")}
                <Err k="companyName" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="GSTIN"  />
                {inp("gstin", "22AALCD9302L1ZK")}
                <Err k="gstin" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Mobile"  />
                {inp("mobile", "9876543210", "tel")}
                <Err k="mobile" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Email"  />
                {inp("email", "contact@company.com", "email")}
                <Err k="email" />
              </div>

              <div className="col-span-2 flex flex-col gap-[5px]" style={{ gridColumn:"span 2" }}>
                <FieldLabel label="Address" required />
                <textarea
                  className={`${INPUT_CLS} resize-none ${errors.address ? INPUT_ERR : ""}`}
                  style={{ ...MONO, minHeight: 68 }}
                  rows={3}
                  value={form.address}
                  placeholder="House No, Area, City, State, PIN"
                  onChange={e => setField("address")(e.target.value)}
                />
                <Err k="address" />
              </div>

            </div>
          </div>

          {/* ── Ship To ── */}
          <div>
            <SectionLabel divider>Ship To Party</SectionLabel>
            <div className="grid grid-cols-2 gap-3">

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Ship-to Company Name" required />
                {inp("shipCompanyName", "Same as above or different")}
                <Err k="shipCompanyName" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Ship-to Phone"  />
                {inp("shipPhone", "9876543210", "tel")}
                <Err k="shipPhone" />
              </div>

              <div className="col-span-2 flex flex-col gap-[5px]">
                <FieldLabel label="Ship-to Address" required />
                <textarea
                  className={`${INPUT_CLS} resize-none ${errors.shipAddress ? INPUT_ERR : ""}`}
                  style={{ ...MONO, minHeight: 68 }}
                  rows={3}
                  value={form.shipAddress}
                  placeholder="Delivery address"
                  onChange={e => setField("shipAddress")(e.target.value)}
                />
                <Err k="shipAddress" />
              </div>

            </div>
          </div>

          {/* ── Contact Person ── */}
          <div>
            <SectionLabel divider>Contact Person</SectionLabel>
            <div className="grid grid-cols-2 gap-3">

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Name" required />
                {inp(null, "e.g. Rahul Sharma", "text", true, "name")}
                <Err k="contactPerson.name" />
              </div>

              <div className="flex flex-col gap-[5px]">
                <FieldLabel label="Phone Number"  />
                {inp(null, "9876543210", "tel", true, "number")}
                <Err k="contactPerson.number" />
              </div>

            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.9)] flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-[9px] rounded-[7px] border border-[#b8b8d4] text-[#1a1a2e] text-[13px] font-semibold hover:border-[#7c6dfa] transition-all"
            style={RAJ}
          >Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-[9px] rounded-[7px] bg-[#40c0f0] text-[#ffffff] text-[13px] font-semibold hover:bg-[#60d0ff] hover:-translate-y-px transition-all shadow-[0_4px_14px_rgba(64,192,240,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
            style={RAJ}
          >
            {saving && (
              <span className="spin w-3 h-3 border-2 border-[#ffffff]/30 border-t-[#ffffff] rounded-full inline-block" />
            )}
            {mode === "edit" ? "Save Changes" : "Add Client"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function ClientsManager() {
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState("");
  const [piClient, setPiClient]   = useState(null); // client selected for PI creation

  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState("add");
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(API_BASE);
      setClients(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load clients", TOAST_CFG);
      console.error("Fetch clients failed", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ── Create ───────────────────────────────────────────────────────────────
  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      const response = await API.post(API_BASE, formData);
      toast.success(response.data.message || "Client created successfully", TOAST_CFG);
      setModalOpen(false);
      await fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error creating client", TOAST_CFG);
      console.error("Create client failed", error);
    } finally {
      setSaving(false);
    }
  };

  // ── Update ───────────────────────────────────────────────────────────────
  const handleUpdate = async (formData) => {
    setSaving(true);
    try {
      const response = await API.put(`${API_BASE}/${editTarget._id}`, formData);
      toast.success(response.data.message || "Client updated successfully", TOAST_CFG);
      setModalOpen(false);
      await fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating client", TOAST_CFG);
      console.error("Update client failed", error);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);
    if (!deleteTarget) return;
    try {
      const response = await API.delete(`${API_BASE}/${deleteTarget._id}`);
      toast.success(response.data.message || "Client deleted successfully", TOAST_CFG);
      await fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting client", TOAST_CFG);
      console.error("Delete client failed", error);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSave = (data) => modalMode === "edit" ? handleUpdate(data) : handleCreate(data);
  const openAdd    = ()    => { setModalMode("add");  setEditTarget(null); setModalOpen(true); };
  const openEdit   = (row) => { setModalMode("edit"); setEditTarget(row);  setModalOpen(true); };
  const openDel    = (row) => { setDeleteTarget(row); setConfirmOpen(true); };
  const openPI     = (row) => setPiClient(row);

  const filtered = search.trim()
    ? clients.filter(c =>
        c.companyName?.toLowerCase().includes(search.toLowerCase()) ||
        c.gstin?.toLowerCase().includes(search.toLowerCase())       ||
        c.email?.toLowerCase().includes(search.toLowerCase())       ||
        c.mobile?.includes(search)                                  ||
        c.contactPerson?.name?.toLowerCase().includes(search.toLowerCase())
      )
    : clients;

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div
        className="min-h-screen bg-[#ffffff]"
        style={{ backgroundImage: "radial-gradient(ellipse 70% 30% at 40% -10%, rgba(64,192,240,0.06), transparent 60%)" }}
      >
        <div className="max-w-[1540px] mx-auto px-4 py-6 md:px-6 md:py-8">

          {/* ── PAGE HEADER ── */}
          <div className="flex items-start justify-between flex-wrap gap-y-3 mb-8 anim-fade-up">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-1.5 h-6 rounded-full bg-gradient-to-b from-[#40c0f0] to-[#7c6dfa]" />
                <h1 className="text-[28px] font-bold text-[#1a1a2e] tracking-[1px]" style={RAJ}>Clients</h1>
              </div>
              <p className="text-[12px] text-[#4a4a75] pl-5" style={MONO}>
                {clients.length} client{clients.length !== 1 ? "s" : ""} registered
              </p>
            </div>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 px-5 py-[10px] rounded-[8px] bg-[#40c0f0] text-[#ffffff] text-[13px] font-bold tracking-[0.5px] hover:bg-[#60d0ff] hover:-translate-y-px transition-all shadow-[0_4px_18px_rgba(64,192,240,0.3)]"
              style={RAJ}
            >
              <span className="text-[16px] leading-none">+</span> Add Client
            </button>
          </div>

          {/* ── STATS STRIP ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 anim-fade-up" style={{ animationDelay: ".05s" }}>
            {[
              { label: "Total Clients",   val: clients.length,                                                                           cls: "text-[#40c0f0]" },
              { label: "With GST",        val: clients.filter(c => c.gstin?.trim()).length,                                              cls: "text-[#40d098]" },
              { label: "Unique Cities",   val: [...new Set(clients.map(c => c.address?.split(",").pop()?.trim()).filter(Boolean))].length, cls: "text-[#7c6dfa]" },
              { label: "Contact Persons", val: clients.filter(c => c.contactPerson?.name).length,                                        cls: "text-[#f0c040]" },
            ].map((s, i) => (
              <div key={i} className="bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl px-5 py-4 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#b8b8d4] to-transparent" />
                <p className="text-[9px] tracking-[2px] uppercase text-[#4a4a75] mb-1" style={MONO}>{s.label}</p>
                <p className={`text-[22px] font-bold ${s.cls}`} style={RAJ}>{s.val}</p>
              </div>
            ))}
          </div>

          {/* ── SEARCH ── */}
          <div className="flex items-center gap-3 mb-4 anim-fade-up" style={{ animationDelay: ".1s" }}>
            <div className="relative flex-1 max-w-[380px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4a75] text-[14px]">⌕</span>
              <input
                className={`${INPUT_CLS} pl-8`}
                style={MONO}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search company, GSTIN, email, mobile…"
              />
            </div>
            {search && (
              <span className="text-[11px] text-[#4a4a75]" style={MONO}>
                {filtered.length} result{filtered.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* ── TABLE ── */}
          <div
            className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden anim-fade-up"
            style={{ animationDelay: ".13s" }}
          >
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#40c0f0] via-[#7c6dfa] to-[#f0c040]" />

            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[1100px]">
                <thead>
                  <tr className="bg-[#eeeef6] border-b border-[#d0d0e8]">
                    {[
                      { h: "#",               a: "center" },
                      { h: "Company Name",    a: "left"   },
                      { h: "GSTIN",           a: "left"   },
                      { h: "Email",           a: "left"   },
                      { h: "Mobile",          a: "left"   },
                      { h: "Ship-to Company", a: "left"   },
                      { h: "Ship Phone",      a: "left"   },
                      { h: "Ship Address",    a: "left"   },
                      { h: "Contact Person",  a: "left"   },
                      { h: "Actions",         a: "center" },
                    ].map(col => (
                      <th
                        key={col.h}
                        className={`px-3 py-[11px] text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] font-medium whitespace-nowrap text-${col.a}`}
                        style={MONO}
                      >{col.h}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
                    : filtered.length === 0
                    ? (
                      <tr>
                        <td colSpan={10} className="text-center py-16">
                          <div className="text-[40px] opacity-20 mb-3">{search ? "⌕" : "🏢"}</div>
                          <p className="text-[14px] font-semibold text-[#1a1a2e] opacity-30 mb-1" style={RAJ}>
                            {search ? "No matches found" : "No clients yet"}
                          </p>
                          <p className="text-[12px] text-[#4a4a75]" style={MONO}>
                            {search ? "Try a different search term" : "Click 'Add Client' to get started"}
                          </p>
                        </td>
                      </tr>
                    )
                    : filtered.map((c, i) => (
                      <tr
                        key={c._id}
                        className="row-enter border-b border-[rgba(208,208,232,0.7)] transition-colors hover:bg-[rgba(64,192,240,0.03)] group"
                        style={{ animationDelay: `${i * 0.04}s` }}
                      >
                        {/* # */}
                        <td className="px-3 py-[11px] text-center text-[11px] text-[#4a4a75]" style={MONO}>{i + 1}</td>

                        {/* Company Name */}
                        <td className="px-3 py-[11px]">
                          <div className="text-[12px] font-semibold text-[#1a1a2e]" style={SANS}>{c.companyName}</div>
                          <div className="text-[10px] text-[#4a4a75] mt-[2px] max-w-[200px] truncate" style={MONO}>{c.address}</div>
                        </td>

                        {/* GSTIN */}
                        <td className="px-3 py-[11px]">
                          <span className="inline-block px-2 py-[2px] rounded-[4px] text-[10px] font-medium bg-[rgba(124,109,250,0.08)] text-[#7c6dfa] border border-[rgba(124,109,250,0.2)]" style={MONO}>
                            {c.gstin}
                          </span>
                        </td>

                        {/* Email */}
                        <td className="px-3 py-[11px] text-[11px] text-[#40c0f0] whitespace-nowrap" style={MONO}>
                          <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                        </td>

                        {/* Mobile */}
                        <td className="px-3 py-[11px] text-[11px] text-[#1a1a2e] whitespace-nowrap" style={MONO}>
                          <a href={`tel:${c.mobile}`} className="hover:text-[#40c0f0] transition-colors">{c.mobile}</a>
                        </td>

                        {/* Ship-to Company */}
                        <td className="px-3 py-[11px]">
                          <div className="text-[11px] font-medium text-[#1a1a2e] max-w-[160px] truncate" style={SANS}>{c.shipCompanyName}</div>
                        </td>

                        {/* Ship Phone */}
                        <td className="px-3 py-[11px] text-[11px] text-[#4a4a75] whitespace-nowrap" style={MONO}>{c.shipPhone}</td>

                        {/* Ship Address */}
                        <td className="px-3 py-[11px]">
                          <div className="text-[11px] text-[#4a4a75] max-w-[160px] truncate" style={MONO}>{c.shipAddress}</div>
                        </td>

                        {/* Contact Person */}
                        <td className="px-3 py-[11px]">
                          <div className="text-[11px] font-semibold text-[#40d098]" style={SANS}>{c.contactPerson?.name}</div>
                          <div className="text-[10px] text-[#4a4a75] mt-[1px]" style={MONO}>{c.contactPerson?.number}</div>
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-[11px] text-center">
                          <div className="flex items-center justify-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openPI(c)}
                              className="inline-flex items-center px-3 py-[5px] rounded-[6px] border border-[rgba(240,192,64,0.35)] text-[#f0c040] text-[11px] font-semibold hover:bg-[rgba(240,192,64,0.1)] transition-all whitespace-nowrap"
                              style={RAJ}
                            >+ PI</button>
                            <button
                              onClick={() => openEdit(c)}
                              className="inline-flex items-center px-3 py-[5px] rounded-[6px] border border-[rgba(64,192,240,0.3)] text-[#40c0f0] text-[11px] font-semibold hover:bg-[rgba(64,192,240,0.1)] transition-all"
                              style={RAJ}
                            >Edit</button>
                            <button
                              onClick={() => openDel(c)}
                              className="inline-flex items-center px-3 py-[5px] rounded-[6px] border border-[rgba(240,80,96,0.3)] text-[#f05060] text-[11px] font-semibold hover:bg-[rgba(240,80,96,0.1)] transition-all"
                              style={RAJ}
                            >Del</button>
                          </div>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>

            {/* Bottom bar */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#d0d0e8] bg-[rgba(240,240,252,0.85)]">
              <span className="text-[10px] text-[#4a4a75]" style={MONO}>
                {loading ? "Loading…" : `${filtered.length} of ${clients.length} clients`}
              </span>
              <button
                onClick={fetchClients}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-[6px] rounded-[6px] border border-[#b8b8d4] text-[#4a4a75] text-[11px] font-semibold hover:border-[#40c0f0] hover:text-[#1a1a2e] transition-all disabled:opacity-40"
                style={RAJ}
              >
                {loading && (
                  <span className="spin w-3 h-3 border-2 border-[#4a4a75]/40 border-t-[#40c0f0] rounded-full inline-block" />
                )}
                Refresh
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── MODALS ── */}
      <ClientModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        saving={saving}
      />

      <ConfirmDialog
        open={confirmOpen}
        clientName={deleteTarget?.companyName}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setDeleteTarget(null); }}
      />

      {/* ── PI CREATOR (full-screen overlay) ── */}
      {piClient && (
        <PICreator
          client={piClient}
          onClose={() => setPiClient(null)}
        />
      )}
    </>
  );
}